// Site Config Context — lets the owner override site text, fonts, colors, etc.
// from Admin → Site Editor. Falls back to static defaults from /lib/site.js when
// an override is not set.

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import * as defaults from "@/lib/site";

const SiteConfigContext = createContext({ cfg: {}, ready: false });

// Curated Google-fonts list the owner can pick from in Site Editor.
// First option per category is the existing default.
export const FONT_OPTIONS = {
    display: [
        { v: "Cabinet Grotesk", label: "Cabinet Grotesk (default)", google: "Cabinet Grotesk", weights: "800;900;700" },
        { v: "Bebas Neue", label: "Bebas Neue", weights: "400" },
        { v: "Oswald", label: "Oswald", weights: "500;600;700" },
        { v: "Anton", label: "Anton", weights: "400" },
        { v: "Archivo Black", label: "Archivo Black", weights: "400" },
        { v: "Russo One", label: "Russo One", weights: "400" },
        { v: "Teko", label: "Teko", weights: "500;600;700" },
        { v: "Black Ops One", label: "Black Ops One", weights: "400" },
        { v: "Saira Condensed", label: "Saira Condensed", weights: "700;800;900" },
        { v: "Bowlby One", label: "Bowlby One", weights: "400" },
    ],
    body: [
        { v: "Manrope", label: "Manrope (default)", weights: "400;500;600;700" },
        { v: "Inter", label: "Inter", weights: "400;500;600;700" },
        { v: "Roboto", label: "Roboto", weights: "400;500;700" },
        { v: "Source Sans 3", label: "Source Sans 3", weights: "400;600;700" },
        { v: "Work Sans", label: "Work Sans", weights: "400;500;600;700" },
        { v: "DM Sans", label: "DM Sans", weights: "400;500;700" },
        { v: "IBM Plex Sans", label: "IBM Plex Sans", weights: "400;500;700" },
        { v: "Rubik", label: "Rubik", weights: "400;500;700" },
    ],
};

// Cabinet Grotesk lives on fontshare; everything else uses Google Fonts.
const _injected = new Set();
function injectFont(family, weights = "400;700") {
    if (!family || _injected.has(family)) return;
    if (family === "Cabinet Grotesk" || family === "Manrope") {
        _injected.add(family);
        return; // already loaded statically in index.css
    }
    const id = `font-${family.replace(/\s+/g, "-")}`;
    if (document.getElementById(id)) {
        _injected.add(family);
        return;
    }
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    const param = `family=${encodeURIComponent(family).replace(/%20/g, "+")}:wght@${weights}`;
    link.href = `https://fonts.googleapis.com/css2?${param}&display=swap`;
    document.head.appendChild(link);
    _injected.add(family);
}

function applyTheme(cfg) {
    const root = document.documentElement;
    const typo = cfg.typography || {};
    const theme = cfg.theme || {};

    const displayFam = typo.display_font || "Cabinet Grotesk";
    const bodyFam = typo.body_font || "Manrope";

    const dOpt = FONT_OPTIONS.display.find((o) => o.v === displayFam);
    const bOpt = FONT_OPTIONS.body.find((o) => o.v === bodyFam);
    injectFont(displayFam, dOpt?.weights);
    injectFont(bodyFam, bOpt?.weights);

    root.style.setProperty("--font-display", `"${displayFam}", "Cabinet Grotesk", "Manrope", sans-serif`);
    root.style.setProperty("--font-body", `"${bodyFam}", "Manrope", sans-serif`);

    if (theme.accent_hex && /^#[0-9a-fA-F]{6}$/.test(theme.accent_hex)) {
        root.style.setProperty("--brand-accent", theme.accent_hex);
    } else {
        root.style.removeProperty("--brand-accent");
    }
}

export function SiteConfigProvider({ children }) {
    const [cfg, setCfg] = useState({});
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API}/site/config`)
            .then((r) => {
                if (!mounted) return;
                const data = r.data || {};
                setCfg(data);
                applyTheme(data);
            })
            .catch(() => {})
            .finally(() => {
                if (mounted) setReady(true);
            });
        return () => {
            mounted = false;
        };
    }, []);

    const value = useMemo(() => ({ cfg, ready, setCfg: (next) => { setCfg(next); applyTheme(next); } }), [cfg, ready]);
    return <SiteConfigContext.Provider value={value}>{children}</SiteConfigContext.Provider>;
}

export function useSiteCfg() {
    return useContext(SiteConfigContext);
}

// Helper: read a nested path from cfg with fallback to defaults from /lib/site.js
// path syntax: "business.phone_display"
export function useSiteText(path, fallback) {
    const { cfg } = useSiteCfg();
    const parts = path.split(".");
    let cur = cfg;
    for (const p of parts) {
        if (cur && typeof cur === "object" && p in cur) cur = cur[p];
        else return fallback;
    }
    if (cur == null || cur === "") return fallback;
    return cur;
}

// Merge SERVICES top-level entries with overrides (services.<slug>.{title,short,long})
export function useServices() {
    const { cfg } = useSiteCfg();
    const overrides = (cfg && cfg.services) || {};
    return defaults.SERVICES.map((s) => {
        const o = overrides[s.slug];
        if (!o) return s;
        return { ...s, ...Object.fromEntries(Object.entries(o).filter(([, v]) => v != null && v !== "")) };
    });
}

// Merge SERVICE_CATALOG entries with overrides (catalog.<slug>.{title,duration,price})
export function useServiceCatalog() {
    const { cfg } = useSiteCfg();
    const overrides = (cfg && cfg.catalog) || {};
    return defaults.SERVICE_CATALOG.map((s) => {
        const o = overrides[s.slug];
        if (!o) return s;
        return { ...s, ...Object.fromEntries(Object.entries(o).filter(([, v]) => v != null && v !== "")) };
    });
}

// Merge BLOG_POSTS with overrides (blog.<slug>.{title,excerpt,date,readTime,body})
export function useBlogPosts() {
    const { cfg } = useSiteCfg();
    const overrides = (cfg && cfg.blog) || {};
    return defaults.BLOG_POSTS.map((p) => {
        const o = overrides[p.slug];
        if (!o) return p;
        const cleaned = Object.fromEntries(
            Object.entries(o).filter(([, v]) => {
                if (v == null || v === "") return false;
                if (Array.isArray(v) && v.length === 0) return false;
                return true;
            }),
        );
        return { ...p, ...cleaned };
    });
}

export function useBlogPost(slug) {
    const posts = useBlogPosts();
    return posts.find((p) => p.slug === slug);
}

// Mapping the static defaults from /lib/site.js to override paths — used by the
// Site Editor to show "current value" placeholders.
export const DEFAULTS_MAP = {
    "business.brand": defaults.BRAND,
    "business.phone_display": defaults.PHONE_DISPLAY,
    "business.phone_e164": defaults.PHONE_E164,
    "business.service_area": defaults.SERVICE_AREA,
    "business.service_area_long": defaults.SERVICE_AREA_LONG,
    "business.instagram_url": defaults.INSTAGRAM_URL,
    "business.tiktok_url": defaults.TIKTOK_URL,
    "business.facebook_url": defaults.FACEBOOK_URL,
    "rev_cash.amount": defaults.REV_CASH_AMOUNT,
};
